import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Stack,
  Select,
  InputLabel,
} from '@mui/material';

// COMPONENT
import TariffLinesList from './line/TariffLineTable';
import TariffLinesSingle from './line/TariffLineSingleView';
import Loader from '../../../common/Loader';

// NETWORK
import useTariffRequest from '../../../../requests/TariffRequest';

export default function TariffPlanSingleView({
  tariffScheme,
  tariffPlan,
  backButtonClick,
  isNew = false,
  errorHandler,
  showTariffLine,
  setShowTariffLine,
  handleParentShowSingleTariffLine = false,
  isEditing = false,
  setIsEditing = () => {},
  setAddNewTariffLine = null,
  addNewTariffLine = false,
}) {
  const [edits, setEdits] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [tariff_lines, setTariffLines] = useState([]);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [tariffSchemes, setTariffSchemes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentTariffPlan, setCurrentTariffPlan] = useState(tariffPlan || {});

  const {
    getPlanExtras,
    getTariffSchemes,
    getTariffLines,
    updateTariffPlan,
    getTariffPlans,
    createNewTariffPlan,
    cancelTariffPlanId,
    deleteTariffPlan,
  } = useTariffRequest();

  // Keep currentTariffPlan in sync with tariffPlan prop
  useEffect(() => {
    if (tariffPlan) {
      setCurrentTariffPlan(tariffPlan);
    }
    return () => {
      setCurrentTariffPlan({});
    };
  }, [tariffPlan]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const extras = await getPlanExtras(errorHandler);
        const tariffSchemes = await getTariffSchemes(errorHandler);

        let tariffLinesResponse = [];
        if (tariffPlan && !isNew) {
          tariffLinesResponse = await getTariffLines(
            tariffScheme.tariff_scheme_id,
            tariffPlan.tariff_plan_id,
            errorHandler
          );
        }

        setTariffLines(tariffLinesResponse);
        setTariffSchemes(tariffSchemes);
        setStatuses(extras.statuses);
        setTypes(extras.types);
        setCurrencies(extras.currencies);
        setTariffPlans(extras.tariff_plans);
        setIsLoading(false);

        if (isNew) {
          setEdits({
            copy_tariff_plan: 'N/A',
            plan_currency: extras.currencies ? extras.currencies[0].code : '',
            plan_status: extras.statuses ? extras.statuses[0].code : '',
            plan_type: extras.types ? extras.types[0].code : '',
          });
        }
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [tariffPlan, tariffScheme, errorHandler, isNew]);

  const handleBackButtonClick = async (refreshFlag, isTariffLine = false) => {
    if (isEditing) {
      toggleEdit();
      return;
    }

    setShowTariffLine(false);
    if (refreshFlag) {
      setIsLoading(true);
      const tariffLines = await getTariffLines(
        tariffScheme.tariff_scheme_id,
        tariffPlan.tariff_plan_id,
        errorHandler
      );
      setTariffLines(tariffLines);
      setIsLoading(false);
    }

    if (isTariffLine) {
      // If coming from a tariff line, just update the state and stay on the plan view
      setCurrentTariffPlan(tariffPlan);
    } else {
      // If not coming from a tariff line, go back to the scheme view
      backButtonClick(refreshFlag, false, false);
    }
  };

  const handleShowSingleTariffLine = (line) => {
    setShowTariffLine(true);
    if (!line) {
      setAddNewTariffLine(true);
      return;
    }

    if (handleParentShowSingleTariffLine) {
      handleParentShowSingleTariffLine(line);
      if (setShowTariffLine) setShowTariffLine(true);
      // Ensure we preserve the current tariff plan state
      setCurrentTariffPlan(tariffPlan);
    }
  };

  const handleAddNewTariffLine = () => {
    setCurrentTariffPlan(tariffPlan);
    setAddNewTariffLine(true);
  };

  const toggleEdit = () => {
    if (isNew) {
      return backButtonClick(false, false, true);
    }
    setIsEditing(!isEditing);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    setEdits((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      setHasError(false);
      setErrorMessage('');

      if (isNew) {
        if (!edits.copy_tariff_plan) {
          setHasError(true);
          setErrorMessage('Select a Plan to copy from.');
          return false;
        }
        if (!edits?.tariff_plan_name) {
          setHasError(true);
          setErrorMessage('Set Plan name.');
          return false;
        }
        if (!edits.commitment_recur || isNaN(edits.commitment_recur)) {
          setHasError(true);
          setErrorMessage('Set Plan Monthly Licencing Commitment.');
          return false;
        }
        if (!edits.commitment_usage || isNaN(edits.commitment_usage)) {
          setHasError(true);
          setErrorMessage('Set Plan Monthly Usage Commitment.');
          return false;
        }
        if (!edits.included_messages || isNaN(edits.included_messages)) {
          setHasError(true);
          setErrorMessage('Set Plan Included Messages.');
          return false;
        }
        if (!edits.included_cf_minutes || isNaN(edits.included_cf_minutes)) {
          setHasError(true);
          setErrorMessage('Set Plan Included CF Minutes.');
          return false;
        }
        if (!edits.plan_currency) {
          setHasError(true);
          setErrorMessage('Select Plan currency.');
          return false;
        }
        if (!edits.plan_status) {
          setHasError(true);
          setErrorMessage('Select Plan status.');
          return false;
        }
        if (!edits.plan_type) {
          setHasError(true);
          setErrorMessage('Select Plan type.');
          return false;
        }

        setIsSaving(true);
        await createNewTariffPlan(
          tariffScheme.tariff_scheme_id,
          edits,
          errorHandler
        );
        setIsSaving(false);
        backButtonClick(true);
      } else {
        if ('tariff_plan_name' in edits && !edits?.tariff_plan_name) {
          setHasError(true);
          setErrorMessage('Select Plan name.');
          return false;
        }
        if (
          'commitment_recur' in edits &&
          (!edits.commitment_recur || isNaN(edits.commitment_recur))
        ) {
          setHasError(true);
          setErrorMessage('Set Plan Monthly Licencing Commitment.');
          return false;
        }
        if (
          'commitment_usage' in edits &&
          (!edits.commitment_usage || isNaN(edits.commitment_usage))
        ) {
          setHasError(true);
          setErrorMessage('Set Plan Monthly Usage Commitment.');
          return false;
        }
        if (
          'included_messages' in edits &&
          (!edits.included_messages || isNaN(edits.included_messages))
        ) {
          setHasError(true);
          setErrorMessage('Set Plan Included Messages.');
          return false;
        }
        if (
          'included_cf_minutes' in edits &&
          (!edits.included_cf_minutes || isNaN(edits.included_cf_minutes))
        ) {
          setHasError(true);
          setErrorMessage('Set Plan Included CF Minutes.');
          return false;
        }

        setIsSaving(true);

        await updateTariffPlan(
          tariffScheme.tariff_scheme_id,
          tariffPlan.tariff_plan_id,
          edits,
          errorHandler
        );

        // Fetch updated data after successful save
        setIsLoading(true);
        const [tariffPlans, tariffLines] = await Promise.all([
          getTariffPlans(tariffScheme.tariff_scheme_id, errorHandler),
          getTariffLines(
            tariffScheme.tariff_scheme_id,
            tariffPlan.tariff_plan_id,
            errorHandler
          ),
        ]);

        // Find the updated plan from the list
        const updatedPlan = tariffPlans.find(
          (plan) => plan.tariff_plan_id === tariffPlan.tariff_plan_id
        );

        setCurrentTariffPlan(updatedPlan);
        setTariffLines(tariffLines);
        setEdits({});
        setIsSaving(false);
        setIsLoading(false);
        setIsEditing(false);
      }
    } catch (e) {
      setIsSaving(false);
      // Handle error
    }
  };

  const handleDelete = async () => {
    await deleteTariffPlan(
      tariffScheme.tariff_scheme_id,
      tariffPlan.tariff_plan_id,
      errorHandler
    );
    backButtonClick(true);
  };

  const cancelTariffPlan = async () => {
    setIsSaving(true);
    await cancelTariffPlanId(
      tariffScheme.tariff_scheme_id,
      tariffPlan.tariff_plan_id,
      errorHandler
    );
    backButtonClick(true);
  };

  const generatePlanSelects = () => {
    const statusList = statuses.map((status) => (
      <MenuItem key={status.code} value={status.code}>
        {status.meaning}
      </MenuItem>
    ));
    const typesList = types.map((type) => (
      <MenuItem key={type.code} value={type.code}>
        {type.meaning}
      </MenuItem>
    ));
    const currenciesList = currencies.map((currency) => (
      <MenuItem key={currency.code} value={currency.code}>
        {currency.meaning}
      </MenuItem>
    ));
    const tariffPlanLists = [
      <MenuItem key="N/A" value="N/A">
        N/A
      </MenuItem>,
      ...tariffPlans.map((plan) => (
        <MenuItem key={plan.tariff_plan_id} value={plan.tariff_plan_id}>
          {plan.select_name}
        </MenuItem>
      )),
    ];
    const tariffSchemeLists = [
      <MenuItem key="N/A" value="N/A">
        N/A
      </MenuItem>,
      ...tariffSchemes.map((scheme) => (
        <MenuItem key={scheme.tariff_scheme_id} value={scheme.tariff_scheme_id}>
          {scheme.tariff_scheme_name}
        </MenuItem>
      )),
    ];

    return {
      statusSelect:
        statuses.length > 0 ? (
          <Select
            fullWidth
            name="plan_status"
            size="small"
            required
            defaultValue={
              !isNew ? currentTariffPlan?.plan_status : statuses[0].code
            }
            onChange={handleFormChange}
            displayEmpty
          >
            {statusList}
          </Select>
        ) : null,
      typeSelect:
        types.length > 0 ? (
          <Select
            fullWidth
            name="plan_type"
            size="small"
            required
            defaultValue={!isNew ? currentTariffPlan?.plan_type : types[0].code}
            onChange={handleFormChange}
            displayEmpty
          >
            {typesList}
          </Select>
        ) : null,
      currencySelect:
        currencies.length > 0 ? (
          <Select
            fullWidth
            name="plan_currency"
            required
            size="small"
            defaultValue={
              !isNew ? currentTariffPlan?.plan_currency : currencies[0].code
            }
            onChange={handleFormChange}
          >
            {currenciesList}
          </Select>
        ) : null,
      copyTariffPlan:
        tariffPlans.length > 0 ? (
          <Select
            fullWidth
            name="copy_tariff_plan"
            required
            size="small"
            defaultValue={!isNew ? currentTariffPlan?.copy_tariff_plan : 'N/A'}
            onChange={handleFormChange}
          >
            {tariffPlanLists}
          </Select>
        ) : null,
      moveToScheme:
        tariffPlans.length > 0 ? (
          <Select
            fullWidth
            name="move_to_scheme_id"
            size="small"
            defaultValue={
              !isNew
                ? currentTariffPlan?.tariff_scheme_id
                : tariffPlans[0].tariff_scheme_id
            }
            onChange={handleFormChange}
            displayEmpty
          >
            {tariffSchemeLists}
          </Select>
        ) : null,
    };
  };

  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  const generateBody = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (!currentTariffPlan && !isNew) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography>No tariff plan selected</Typography>
        </Box>
      );
    }

    if (addNewTariffLine) {
      return (
        <TariffLinesSingle
          tariffPlan={currentTariffPlan}
          backButtonClick={handleBackButtonClick}
          isNew={addNewTariffLine}
          errorHandler={errorHandler}
          setIsEditing={setIsEditing}
        />
      );
    } else if (isNew || isEditing) {
      return (
        <Box component="form" onSubmit={handleSubmit} maxWidth={800}>
          <Grid container spacing={3}>
            {isNew && (
              <Grid size={12}>
                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 250 }} required>
                    Copy Tariff Lines from Plan
                  </InputLabel>
                  {generatePlanSelects().copyTariffPlan}
                </Stack>
              </Grid>
            )}
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Name
                </InputLabel>
                <TextField
                  fullWidth
                  name="tariff_plan_name"
                  required
                  defaultValue={!isNew ? tariffPlan?.tariff_plan_name : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Monthly Licensing Commitment
                </InputLabel>
                <TextField
                  fullWidth
                  name="commitment_recur"
                  type="text"
                  required
                  defaultValue={!isNew ? tariffPlan?.commitment_recur : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Monthly Usage Commitment
                </InputLabel>
                <TextField
                  fullWidth
                  name="commitment_usage"
                  type="text"
                  required
                  defaultValue={!isNew ? tariffPlan?.commitment_usage : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Included Messages
                </InputLabel>
                <TextField
                  fullWidth
                  name="included_messages"
                  type="text"
                  required
                  defaultValue={!isNew ? tariffPlan?.included_messages : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Included CF Minutes
                </InputLabel>
                <TextField
                  fullWidth
                  name="included_cf_minutes"
                  type="text"
                  required
                  defaultValue={!isNew ? tariffPlan?.included_cf_minutes : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }}>Included Numbers</InputLabel>
                <TextField
                  fullWidth
                  name="max_users"
                  type="text"
                  defaultValue={!isNew ? tariffPlan?.max_users : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Currency
                </InputLabel>
                {generatePlanSelects().currencySelect}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Status
                </InputLabel>
                {generatePlanSelects().statusSelect}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 250 }} required>
                  Type
                </InputLabel>
                {generatePlanSelects().typeSelect}
              </Stack>
            </Grid>

            {isEditing && (
              <Grid size={12}>
                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 250 }} required>
                    Move Plan to Another Scheme
                  </InputLabel>
                  {generatePlanSelects().moveToScheme}
                </Stack>
              </Grid>
            )}
          </Grid>

          {hasError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() =>
                !isNew ? toggleEdit() : handleBackButtonClick(false)
              }
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box>
          <Grid container spacing={2}>
            {generateFormGroup(
              'Name:',
              currentTariffPlan?.tariff_plan_name || 'N/A'
            )}
            {generateFormGroup(
              'Monthly Licencing Commitment:',
              currentTariffPlan?.commitment_recur || 'N/A'
            )}
            {generateFormGroup(
              'Monthly Usage Commitment:',
              currentTariffPlan?.commitment_usage || 'N/A'
            )}
            {generateFormGroup(
              'Included Messages:',
              currentTariffPlan?.included_messages || 'N/A'
            )}
            {generateFormGroup(
              'Included CF Minutes:',
              currentTariffPlan?.included_cf_minutes || 'N/A'
            )}
            {generateFormGroup(
              'Included Numbers:',
              currentTariffPlan?.max_users || 'N/A'
            )}
            {generateFormGroup(
              'Currency:',
              currentTariffPlan?.plan_currency_name || 'N/A'
            )}
            {generateFormGroup(
              'Status:',
              currentTariffPlan?.plan_status_name || 'N/A'
            )}
            {generateFormGroup(
              'Type:',
              currentTariffPlan?.plan_type_name || 'N/A'
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" color="secondary" onClick={toggleEdit}>
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowConfirmation(true)}
            >
              Cancel Plan
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <TariffLinesList
              tariffLines={tariff_lines}
              showSingleTariffLine={handleShowSingleTariffLine}
              addNewTariffLine={handleAddNewTariffLine}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box>
      {/* Main Content */}
      {generateBody()}

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      >
        <DialogTitle variant="h2">Cancellation Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to cancel this tariff plan?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmation(false)}
            color="secondary"
            variant="outlined"
          >
            No
          </Button>
          <Button
            onClick={cancelTariffPlan}
            color="secondary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
